import styled from "styled-components";

import { shimmer } from "shared/components/molecules/LoadingSkeleton/atoms/styles";

interface ICarouselFallbackBackgroundProps {
  backgroundColor: string;
}

export const CarouselFallbackBackground = styled.div<ICarouselFallbackBackgroundProps>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  background-color: ${({ backgroundColor }) => backgroundColor ?? "#303030"};
`;

export const CarouselContainer = styled.div`
  margin-bottom: 40px;

  @media (max-width: 1366px) {
    margin-bottom: 29px;
  }

  @media (max-width: 500px) {
    margin-bottom: 24px;
  }

  .carousel-title {
    align-items: center;
    color: #ffffffe5 !important;
    font: 600 1.5rem/2.25rem "Inter" !important;
    display: flex;
    text-align: center;
    margin: 0 0 0 20px;
    position: relative;

    text-shadow: 0px 0px 48px #00000080;

    transition: 0.3s all;

    :hover {
      color: #ffffff !important;
    }

    @media (max-width: 1600px) {
      font: 700 1rem/1.5rem "Inter" !important;
    }

    @media (max-width: 400px) {
      font: 600 0.875rem/1.3125rem "Inter" !important;
    }

    @media (min-width: 600px) {
      margin: 0 0 0 55px;
    }
  }

  .carousel-subtitle {
    color: rgba(255, 255, 255, 0.6) !important;
    font: 400 1.125rem/150% "Inter";
    display: flex;
    margin: 0 0 0 20px;
    position: relative;

    @media (min-width: 600px) {
      margin: 0 0 0 55px;
    }

    @media (max-width: 1600px) {
      font: 400 0.875rem/1.3125rem "Inter" !important;
    }
  }

  img {
    height: 100%;

    &[data-is-loading="true"] {
      background: linear-gradient(90deg, #2f2f2f 25%, #424242 50%, #2f2f2f 75%);
      background-size: 200% 100%;

      animation: ${shimmer} 1.5s infinite ease-in-out;
    }
  }

  .horizontal {
    img,
    .browse-carousel-fallback {
      max-height: 187.31px;
      object-fit: cover;
      max-width: initial;

      @media (min-width: 2250px) {
        max-height: 700px;

        height: 100%;
      }

      @media (max-width: 1366px) {
        max-height: 131.64px;
      }

      @media (max-width: 500px) {
        max-height: 102.39px;
      }
    }

    .swiper-slide {
      :has(.browse-carousel-fallback) {
        height: initial;

        .browse-carousel-fallback {
          height: 100%;
          width: 100%;
        }
      }
    }
  }

  .vertical {
    img,
    .browse-carousel-fallback {
      aspect-ratio: 9 / 12 !important;
      object-fit: cover;
      max-height: 477.01px;
      max-width: initial;

      @media (min-width: 1951px) {
        max-height: 500px;
      }

      @media (min-width: 2250px) {
        max-height: 700px;
      }

      @media (max-width: 1366px) {
        max-height: 293.91px;
      }

      @media (max-width: 500px) {
        max-height: 221.54px;
      }
    }

    .swiper-slide:has(.browse-carousel-fallback) {
      height: initial;

      .browse-carousel-fallback {
        height: 100%;
        width: 100%;
      }
    }
  }

  .browse-carousel-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  img,
  ${CarouselFallbackBackground}, .swiper-slide > div {
    border-radius: 12px;

    @media (max-width: 1366px) {
      border-radius: 2.81px;
    }

    @media (max-width: 600px) {
      border-radius: 4px;
    }
  }

  .swiper-slide > div {
    width: 100%;
    height: 100%;
  }

  .swiper-slide > div {
    position: relative;

    &::before {
      content: "";
      position: absolute;
      top: -220%;
      left: 0%;
      width: 200%;
      height: 200%;
      background: linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.2),
        rgba(255, 255, 255, 0)
      );
      transform: rotate(25deg);
      transition: all 0.25s ease-in-out;
      filter: blur(4px);
      z-index: 9999;

      pointer-events: none;
    }
    overflow: hidden;

    :hover {
      ::before {
        top: -140%;
        left: -20%;
      }
    }
  }

  img {
    width: 100%;
    object-fit: cover;
  }
`;

export const NavButton = styled.span`
  width: 1.875rem;
  height: 100%;
  display: flex;
  cursor: pointer;
  user-select: none;
  align-items: center;
  justify-content: center;
  transition: all ease-in-out 0.2s;
  background-color: #14141432;

  @media (min-width: 600px) {
    width: 3.375rem;
  }

  :hover {
    background-color: #14141480;

    svg {
      visibility: visible;

      width: 2.5rem;
      height: 2.5rem;
    }
  }

  svg {
    transition: all ease-in-out 0.2s;

    filter: drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.3));
    visibility: collapse;
  }
`;

export const SlidesWrapper = styled.div`
  position: relative;

  margin-top: 12px;

  width: calc(100vw - 75px);
  margin-left: 55px;

  > .swiper {
    overflow: visible;
  }

  @media (max-width: 600px) {
    width: calc(100vw - 55px);
    margin-left: 20px;
  }

  .swiper-button-prev,
  .swiper-button-next {
    height: 100%;
    color: #f2f2f28f;
    top: 0;
    margin: 0;
    padding: 0 2rem;

    transition: 0.3s all;

    @media (max-width: 600px) {
      display: none;
    }

    @media (min-width: 1000px) {
      :hover {
        background-color: #00000026;
      }
    }

    &.swiper-button-disabled {
      opacity: 0;
    }
  }

  .swiper-button-prev {
    left: -55px;
  }

  .swiper-button-next {
    right: -10px;
  }
`;
